import { Flex, Grid, LoadingOverlay, Text, Title } from "@dehub/theme";
import { modals, useForm } from "@dehub/theme";
import { yupResolver } from "mantine-form-yup-resolver";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { WarehouseApi } from "@/apis";
import { message } from "@/utils/message";
import { renderName } from "@/utils/render-name";
import { WarehouseAddressSection } from "./warehouse-address-section";
import { WarehouseBuyerSupplierConfig } from "./warehouse-buyer-supplier-config";
import { WarehouseContactSection } from "./warehouse-contact-section";
import { WarehouseFormActions } from "./warehouse-form-actions";
import { WarehouseLocationMap } from "./warehouse-location-map";
import { WarehouseMainSection } from "./warehouse-main-section";

export type IFormData = {
  partnerRef: string;
  regUserName: string;
  name: string;
  branchId: string | null;
  province: string;
  district: string;
  khoroo: string;
  khoroolol: string;
  khotkhonBair: string;
  khashaaDavkhar: string;
  khaalgaDugaar: string;
  warehouseAddress: string;
  warehouseUserId: string;
  isDefault: boolean | string;
  isBuyer: boolean | string;
  isSupplier: boolean | string;
  buyers: string[];
  suppliers: string[];
  phone: string;
  refCode: string;
  locationLat: string;
  locationLng: string;
  isConfirmed: boolean;
};

export type IBranchRow = {
  id: string;
  name: string;
  province?: string;
  district?: string;
  khoroo?: string;
  khoroolol?: string;
  khotkhonBair?: string;
  khashaaDavkhar?: string;
  khaalgaDugaar?: string;
  address?: string;
  locationLat?: string | number;
  locationLng?: string | number;
  buyers?: { id: string; refCode: string; profileName: string; profileNameEng?: string | null }[];
  suppliers?: { id: string; refCode: string; profileName: string; profileNameEng?: string | null }[];
};

export type IWarehouse = {
  id: string;
  partner?: { refCode?: string };
  regUser?: { firstName?: string; lastName?: string };
  name?: string;
  branchId?: string;
  province?: string;
  district?: string;
  khoroo?: string;
  khoroolol?: string;
  khotkhonBair?: string;
  khashaaDavkhar?: string;
  khaalgaDugaar?: string;
  warehouseAddress?: string;
  warehouseUserId?: string;
  phone?: string;
  isDefault?: boolean;
  isBuyer?: boolean;
  isSupplier?: boolean;
  buyers?: { id: string }[];
  suppliers?: { id: string }[];
  refCode?: string;
  locationLat?: string;
  locationLng?: string;
  isConfirmed?: boolean;
  warehouseStatus?: string;
};

/** What the page header can call on the form while it is in edit mode. */
export type WarehouseFormHandle = {
  /** Validate and save; the warehouse keeps its current status (active stays active). */
  save: () => void;
  /** Validate, ask for confirmation, save and activate. */
  activate: () => void;
};

type Props = {
  onClose: () => void;
  reload: () => void;
  warehouse?: IWarehouse;
  branches?: { rows?: IBranchRow[] };
  isDetail?: boolean;
  /** Hide the bottom save / confirm row: the page header drives the form through `handleRef`. */
  hideActions?: boolean;
  handleRef?: React.MutableRefObject<WarehouseFormHandle | null>;
  /** True while a save started from the page header is running. */
  onBusyChange?: (busy: boolean) => void;
};

function buildSchema(tCommon: (key: string) => string) {
  return yup.object({
    partnerRef: yup.string().trim().nullable(),
    regUserName: yup.string().trim().nullable(),
    name: yup.string().required(tCommon("required")),
    branchId: yup.string().required(tCommon("required")),
    province: yup.string().required(tCommon("required")),
    district: yup.string().typeError(tCommon("required")).required(tCommon("required")),
    khoroo: yup.string().required(tCommon("required")),
    khoroolol: yup.string().required(tCommon("required")),
    khotkhonBair: yup.string().required(tCommon("required")),
    khashaaDavkhar: yup.string().required(tCommon("required")),
    khaalgaDugaar: yup.string().required(tCommon("required")),
    warehouseAddress: yup.string().required(tCommon("required")),
    phone: yup.string().matches(/^[986]\d{7}$/, tCommon("validation.invalidPhone")).required(tCommon("required")),
    warehouseUserId: yup.string().nullable(),
    refCode: yup.string().nullable(),
    isDefault: yup.boolean().nullable(),
    isBuyer: yup.boolean().nullable(),
    isSupplier: yup.boolean().nullable(),
    buyers: yup.array().when("isBuyer", { is: true, then: (f) => f.required(tCommon("required")), otherwise: (f) => f.notRequired() }),
    suppliers: yup.array().when("isSupplier", { is: true, then: (f) => f.required(tCommon("required")), otherwise: (f) => f.notRequired() }),
    locationLat: yup.number().typeError(tCommon("validation.numberOnly")).required(tCommon("required")),
    locationLng: yup.number().typeError(tCommon("validation.numberOnly")).required(tCommon("required")),
  });
}

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <Grid.Col span={12}>
    <Title order={3} fz="var(--mantine-font-size-lg)" fw={650} mt="xs">
      {children}
    </Title>
  </Grid.Col>
);

export function WarehouseForm({ onClose, reload, warehouse, branches, isDetail, hideActions = false, handleRef, onBusyChange }: Props) {
  const { t } = useTranslation("warehouse");
  const { t: tCommon } = useTranslation("common");
  const [loading, setLoading] = useState<boolean>(false);
  // A branch picked over an existing address asks before the address is copied (it would overwrite the typed one).
  const [pendingBranch, setPendingBranch] = useState<IBranchRow | null>(null);
  const schema = React.useMemo(() => buildSchema(tCommon), [tCommon]);

  const form = useForm<IFormData>({
    initialValues: {
      partnerRef: warehouse?.partner?.refCode ?? "",
      regUserName: warehouse?.regUser ? renderName(warehouse.regUser) : "",
      name: warehouse?.name ?? "",
      branchId: warehouse?.branchId ?? null,
      province: warehouse?.province ?? "",
      district: warehouse?.district ?? "",
      khoroo: warehouse?.khoroo ?? "",
      khoroolol: warehouse?.khoroolol ?? "",
      khotkhonBair: warehouse?.khotkhonBair ?? "",
      khashaaDavkhar: warehouse?.khashaaDavkhar ?? "",
      khaalgaDugaar: warehouse?.khaalgaDugaar ?? "",
      warehouseAddress: warehouse?.warehouseAddress ?? "",
      warehouseUserId: warehouse?.warehouseUserId ?? "",
      phone: warehouse?.phone ?? "",
      isDefault: warehouse?.isDefault ?? false,
      isBuyer: warehouse?.isBuyer?.toString() ?? "false",
      isSupplier: warehouse?.isSupplier?.toString() ?? "false",
      buyers: warehouse?.buyers?.map((c) => c.id) ?? [],
      suppliers: warehouse?.suppliers?.map((c) => c.id) ?? [],
      refCode: warehouse?.refCode ?? "",
      locationLat: warehouse?.locationLat ?? "47.9192",
      locationLng: warehouse?.locationLng ?? "106.9172",
      isConfirmed: warehouse?.isConfirmed ?? false,
    },
    validate: yupResolver(schema),
  });

  const onSubmit = async (values: IFormData, isConfirmed: boolean) => {
    setLoading(true);
    onBusyChange?.(true);
    try {
      const payload = {
        name: values.name,
        branchId: values.branchId!,
        province: values.province,
        district: values.district,
        khoroo: values.khoroo,
        khoroolol: values.khoroolol,
        khotkhonBair: values.khotkhonBair,
        khashaaDavkhar: values.khashaaDavkhar,
        khaalgaDugaar: values.khaalgaDugaar,
        warehouseAddress: values.warehouseAddress,
        warehouseUserId: values.warehouseUserId || null,
        phone: values.phone || "",
        buyers: values.buyers ?? [],
        suppliers: values.suppliers ?? [],
        isDefault: JSON.parse(values.isDefault as string),
        isBuyer: JSON.parse(values.isBuyer as string),
        isSupplier: JSON.parse(values.isSupplier as string),
        locationLat: values.locationLat,
        locationLng: values.locationLng,
        isConfirmed,
      };

      if (warehouse) {
        await WarehouseApi.update(warehouse.id, payload);
      } else {
        await WarehouseApi.create(payload);
      }
      message.success(tCommon("success"));
      onClose();
      reload();
    } catch (error: any) {
      message.error(error?.message || tCommon("error"));
    } finally {
      setLoading(false);
      onBusyChange?.(false);
    }
  };

  const validateThenSubmit = (isConfirmed: boolean) => {
    const result = form.validate();
    if (result.hasErrors) {
      const firstError = Object.values(result.errors).find(Boolean);
      message.error(typeof firstError === "string" ? firstError : tCommon("validation.fillRequiredFields"));
      return;
    }
    onSubmit(form.values, isConfirmed);
  };

  // The header buttons of the detail page call these. The latest closure is kept on every render.
  useEffect(() => {
    if (!handleRef) return;
    handleRef.current = {
      save: () => validateThenSubmit(warehouse?.warehouseStatus === "ACTIVE"),
      activate: () =>
        modals.openConfirmModal({
          title: tCommon("confirm.title"),
          children: <Text size="sm">{t("form.confirmText")}</Text>,
          labels: { confirm: tCommon("confirm.confirm"), cancel: tCommon("confirm.back") },
          centered: true,
          onConfirm: () => validateThenSubmit(true),
        }),
    };
  });

  return (
    <form onSubmit={form.onSubmit(() => { })} noValidate>
      <Flex style={{ position: "relative" }} direction="column" gap="md">
        <LoadingOverlay visible={loading} />
        <Grid>
          <WarehouseMainSection form={form} branches={branches} isDetail={isDetail} pendingBranch={pendingBranch} onPendingBranchChange={setPendingBranch} />
          <WarehouseAddressSection form={form} isDetail={isDetail} />
          <WarehouseLocationMap form={form} isDetail={isDetail} />
          <WarehouseContactSection form={form} isDetail={isDetail} />
          <SectionHeader>{t("form.warehouseConfig")}</SectionHeader>
          <WarehouseBuyerSupplierConfig form={form} branches={branches} isDetail={isDetail} />
        </Grid>
        {!isDetail && !hideActions && <WarehouseFormActions form={form} onSubmit={onSubmit} />}
      </Flex>
    </form>
  );
}
